import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { CompanyService, SelectedUserService, UsersService } from 'app/services';

import { ConfirmCompanyAdminDialogComponent } from '../../dialogs';
import { FormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { takeUntil } from 'rxjs/operators';

@Component({
  standalone: false,
  selector: 'app-user-info',
  templateUrl: './user-info.component.html',
  styleUrls: ['./user-info.component.scss'],
})
export class UserInfoComponent implements OnInit, OnDestroy {
  @ViewChild('emailField', { static: false }) emailInput: ElementRef;
  @Input()
  bordercss: boolean;
  @Input()
  error: string;
  @Output()
  loader = new EventEmitter<boolean>();
  isAdmin: boolean = false; // Initial state
  public userEmailConfirmation = new FormControl();
  public verifyingEmail = false;
  public verifyEmailError = false;
  public showAdminToggle = false;
  public user = null;
  private onDestroy$ = new Subject<void>();

  constructor(
    private _selectedUserService: SelectedUserService,
    private _changeDetectorRef: ChangeDetectorRef,
    private readonly _companyService: CompanyService,
    private _translateService: TranslateService,
    private _dialog: MatDialog,
    private _userService: UsersService,
    private _router: Router
  ) {}

  ngOnInit() {
    this.subscribeToUser();
  }

  ngOnDestroy() {
    this.onDestroy$.next();
  }

  subscribeToUser(): void {
    this.error = ''

    const isExternalUser = (email: string | undefined): boolean => {
      return email ? !email.endsWith('@allegion.com') : false;
    };

    this._selectedUserService.selectedUser$
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((user) => {
        if (user) {
          this.error = '';
          this.user = user;
          this.loader.emit(false);

          const userIsExternal = isExternalUser(this.user.email);

          this.isAdmin = this.user.emailVerified && this.user.isCompanyAdmin && userIsExternal;
          this.showAdminToggle = this.user.emailVerified && userIsExternal;
        }
      });
    this._selectedUserService.error$
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((error) => {
        if (error) {
          this.loader.emit(false);
          this.error = error;
        }
      });
  }

  showVerifyEmailInput(): void {
    this._selectedUserService.verifyUserEmail(this.user.id);
    this.verifyingEmail = true;
    this._changeDetectorRef.detectChanges();
    this.emailInput.nativeElement.focus();
  }
  toggleAdmin() {
    if (this.isAdmin) {
      this.makeAdmin();
    } else {
      this.removeAdmin();
    }
  }
  removeAdmin(): void {
    // translate strings
    let message;
    this._translateService
      .get('MAKE_ADMIN_DIALOG.remove_message')
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((value) => (message = value));
    let title;
    this._translateService
      .get('MAKE_ADMIN_DIALOG.remove_title')
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((value) => (title = value));
    const dialog = this._dialog.open(ConfirmCompanyAdminDialogComponent, {
      disableClose: true,
      autoFocus: false,
      width: '400px',
      data: {
        title,
        message,
        adding: false,
      },
    });

    dialog.afterClosed().subscribe((result) => {
      if (result) {
        this.isAdmin = false;
        this.loader.emit(true);
        this._userService.addRemoveAdmin(
          this.user.id,
          false,
          this.user.companyId
        ).pipe(takeUntil(this.onDestroy$)).subscribe(data => {
          if(data){
            this._companyService.getCompanyBranchesWithUsers(this.user.companyId);
          }
        });
      }
      else{
        this.isAdmin = this.user.isCompanyAdmin;
      }
    });
  }

  goToCompany() {
    this._router.navigate(['company', this.user.companyId]);
    this._dialog.closeAll();
  }

  makeAdmin(): void {
    // translate strings
    let message;
    if (this.user.isBranchAdmin) {
      this._translateService
        .get('MAKE_ADMIN_DIALOG.remove_branch_admin')
        .pipe(takeUntil(this.onDestroy$))
        .subscribe((value) => (message = value));
    }
    else {
      this._translateService
        .get('MAKE_ADMIN_DIALOG.add_message')
        .pipe(takeUntil(this.onDestroy$))
        .subscribe((value) => (message = value));
    }

    let title;
    this._translateService
      .get('MAKE_ADMIN_DIALOG.add_title')
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((value) => (title = value));

    const dialog = this._dialog.open(ConfirmCompanyAdminDialogComponent, {
      disableClose: true,
      autoFocus: false,
      width: '400px',
      data: {
        title,
        message,
        adding: true,
      },
    });

    dialog.afterClosed().subscribe((result) => {
      if (result) {
        this.isAdmin = true;
        this.loader.emit(true);
        this._userService.addRemoveAdmin(
          this.user.id,
          true,
          this.user.companyId
        ).pipe(takeUntil(this.onDestroy$)).subscribe(data => {
          if(data){
            this._companyService.getCompanyBranchesWithUsers(this.user.companyId);
          }
        });;
      }else{
        this.isAdmin = this.user.isCompanyAdmin;
      }
    });
  }
}
